import { useSearchParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { 
  Rocket, 
  Loader2, 
  Download, 
  FileText,
  Target,
  TrendingUp,
  Building2,
  Calendar,
  ArrowLeft
} from "lucide-react";
import { AttachReportToListing } from '@/components/admin/AttachReportToListing';

const translations = {
  en: {
    title: "Strategic Plan Generator",
    subtitle: "Generate personalized growth and development plans using AI",
    companyProfile: "Company Profile",
    companyName: "Company Name",
    industry: "Industry",
    location: "Location",
    description: "Business Description",
    revenue: "Current Annual Revenue ($)",
    employees: "Number of Employees",
    yearsInBusiness: "Years in Business",
    currentChallenges: "Current Challenges",
    growthGoals: "Growth Goals / Vision",
    targetRevenue: "Target Revenue (3 years)",
    generate: "Generate Plan",
    generating: "Generating...",
    downloadPdf: "Download PDF",
    preview: "Preview",
    result: "Generated Strategic Plan",
    success: "Strategic plan generated successfully",
    error: "Error generating plan",
    year1: "Year 1",
    year3: "Year 3",
    year5: "Year 5",
    timeline: "Timeline",
  },
  fr: {
    title: "Générateur de Plans Stratégiques",
    subtitle: "Générez des plans de croissance et de développement personnalisés avec l'IA",
    companyProfile: "Profil de l'Entreprise",
    companyName: "Nom de l'Entreprise",
    industry: "Industrie",
    location: "Localisation",
    description: "Description de l'Activité",
    revenue: "Chiffre d'Affaires Actuel ($)",
    employees: "Nombre d'Employés",
    yearsInBusiness: "Années d'Activité",
    currentChallenges: "Défis Actuels",
    growthGoals: "Objectifs de Croissance / Vision",
    targetRevenue: "CA Cible (3 ans)",
    generate: "Générer le Plan",
    generating: "Génération...",
    downloadPdf: "Télécharger PDF",
    preview: "Aperçu",
    result: "Plan Stratégique Généré",
    success: "Plan stratégique généré avec succès",
    error: "Erreur lors de la génération",
    year1: "Année 1",
    year3: "Année 3",
    year5: "Année 5",
    timeline: "Calendrier",
  },
};

const industries = [
  "Technology", "Healthcare", "Manufacturing", "Retail", "Food & Beverage",
  "Construction", "Professional Services", "Transportation", "Finance", "Real Estate"
];

export default function StrategicPlanGenerator() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  const [formData, setFormData] = useState({
    companyName: "",
    industry: "",
    location: "",
    description: "",
    revenue: "",
    employees: "",
    yearsInBusiness: "",
    currentChallenges: "",
    growthGoals: "",
    targetRevenue: "",
  });

  const dealIdParam = searchParams.get("deal");

  useEffect(() => {
    if (!dealIdParam) return;
    (async () => {
      let snap = await getDoc(doc(db, "dealSubmissions", dealIdParam));
      if (!snap.exists()) snap = await getDoc(doc(db, "deals", dealIdParam));
      if (snap.exists()) {
        const d = snap.data() as any;
        const g = d.rawFormData?.general || {};
        setFormData(prev => ({
          ...prev,
          companyName: g.legalName || d.businessName || prev.companyName,
          industry: g.industry || d.industry || prev.industry,
          location: [g.locationCity, g.locationCountry].filter(Boolean).join(", ") || prev.location,
          description: g.description || d.description || prev.description,
          revenue: String(d.rawFormData?.financials?.revenueYear1 || d.revenueYear1 || prev.revenue),
          yearsInBusiness: g.yearEstablished ? String(new Date().getFullYear() - g.yearEstablished) : prev.yearsInBusiness,
          currentChallenges: d.rawFormData?.culture?.currentChallenges || prev.currentChallenges,
          growthGoals: d.rawFormData?.culture?.growthGoals || prev.growthGoals,
        }));
      }
    })();
  }, [dealIdParam]);

  const handleGenerate = async () => {
    if (!formData.companyName || !formData.industry) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { postApi } = await import('@/lib/api');
      const result = await postApi('/ai/generate-strategic-plan', {
        companyProfile: {
          name: formData.companyName,
          industry: formData.industry,
          location: formData.location,
          description: formData.description,
          currentRevenue: parseFloat(formData.revenue) || 0,
          employees: parseInt(formData.employees) || 0,
          yearsInBusiness: parseInt(formData.yearsInBusiness) || 0,
          currentChallenges: formData.currentChallenges,
          growthGoals: formData.growthGoals,
          targetRevenue: parseFloat(formData.targetRevenue) || 0,
        },
        language,
      });
      if (!result?.success) throw new Error('Strategic plan generation failed');
      setResult(result.strategicPlan);

      toast({ title: t.success });
    } catch (error: any) {
      console.error("Strategic plan generation error:", error);
      toast({ title: t.error, description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow && result) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Strategic Plan - ${formData.companyName}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
              h1 { color: #1D314D; border-bottom: 2px solid #CEAB65; padding-bottom: 10px; }
              h2 { color: #2E4663; margin-top: 30px; }
              h3 { color: #CEAB65; }
              p, li { margin: 10px 0; }
              .section { margin-bottom: 30px; page-break-inside: avoid; }
              ul { padding-left: 20px; }
            </style>
          </head>
          <body>
            <h1>Strategic Growth Plan: ${formData.companyName}</h1>
            <p><strong>Industry:</strong> ${formData.industry}</p>
            <p><strong>Location:</strong> ${formData.location}</p>
            <hr/>
            ${Object.entries(result).map(([key, value]) => `
              <div class="section">
                <h2>${key.replace(/([A-Z])/g, ' $1').trim()}</h2>
                <p>${typeof value === 'object' ? JSON.stringify(value, null, 2) : value}</p>
              </div>
            `).join("")}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const renderSection = (title: string, content: any) => {
    if (!content) return null;
    
    return (
      <div className="border-b border-gray-700 pb-4 mb-4 last:border-0">
        <h3 className="text-amber-500 font-semibold mb-2">{title}</h3>
        {typeof content === 'string' ? (
          <p className="text-gray-300 text-sm whitespace-pre-wrap">{content}</p>
        ) : Array.isArray(content) ? (
          <ul className="list-disc list-inside space-y-1">
            {content.map((item, i) => (
              <li key={i} className="text-gray-300 text-sm">{typeof item === 'string' ? item : JSON.stringify(item)}</li>
            ))}
          </ul>
        ) : (
          <pre className="text-gray-300 text-sm whitespace-pre-wrap">{JSON.stringify(content, null, 2)}</pre>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6">
      {dealIdParam && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/staff/deals/${dealIdParam}`)}
          className="text-gray-300 hover:text-white hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {language === "fr" ? "Retour au dossier" : "Back to Deal"}
        </Button>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">{t.title}</h1>
          <p className="text-gray-400 mt-1">{t.subtitle}</p>
        </div>
        <Rocket className="h-8 w-8 text-amber-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-500" />
              {t.companyProfile}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">{t.companyName} *</Label>
                <Input
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">{t.industry} *</Label>
                <Select
                  value={formData.industry}
                  onValueChange={(v) => setFormData({ ...formData, industry: v })}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">{t.location}</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">{t.yearsInBusiness}</Label>
                <Input
                  type="number"
                  value={formData.yearsInBusiness}
                  onChange={(e) => setFormData({ ...formData, yearsInBusiness: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.description}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">{t.revenue}</Label>
                <Input
                  type="number"
                  value={formData.revenue}
                  onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">{t.employees}</Label>
                <Input
                  type="number"
                  value={formData.employees}
                  onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">{t.targetRevenue}</Label>
                <Input
                  type="number"
                  value={formData.targetRevenue}
                  onChange={(e) => setFormData({ ...formData, targetRevenue: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.currentChallenges}</Label>
              <Textarea
                value={formData.currentChallenges}
                onChange={(e) => setFormData({ ...formData, currentChallenges: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white min-h-[80px]"
                placeholder="List current business challenges..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.growthGoals}</Label>
              <Textarea
                value={formData.growthGoals}
                onChange={(e) => setFormData({ ...formData, growthGoals: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white min-h-[80px]"
                placeholder="Describe growth objectives and vision..."
              />
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t.generating}
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4 mr-2" />
                  {t.generate}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{t.result}</CardTitle>
              {result && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  className="border-amber-500 text-amber-500 hover:bg-amber-500/20"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t.downloadPdf}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {result ? (
              <>
                <AttachReportToListing
                  reportType="strategic_plan"
                  reportContent={result}
                  reportTitle={`Strategic Plan: ${formData.companyName}`}
                  language={language as 'en' | 'fr'}
                />
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-gray-700 mb-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="strategies">Strategies</TabsTrigger>
                  <TabsTrigger value="timeline">{t.timeline}</TabsTrigger>
                  <TabsTrigger value="kpis">KPIs</TabsTrigger>
                </TabsList>
                
                <div className="max-h-[500px] overflow-y-auto">
                  <TabsContent value="overview">
                    {renderSection("Executive Summary", result.executiveSummary)}
                    {renderSection("Current Situation Analysis", result.situationAnalysis)}
                    {renderSection("Strategic Objectives", result.strategicObjectives)}
                  </TabsContent>
                  
                  <TabsContent value="strategies">
                    {renderSection("Growth Strategies", result.growthStrategies)}
                    {renderSection("Action Plan", result.actionPlan)}
                    {renderSection("Risk Analysis", result.riskAnalysis)}
                  </TabsContent>
                  
                  <TabsContent value="timeline">
                    {renderSection("Implementation Timeline", result.implementationTimeline)}
                    {renderSection("Milestones", result.milestones)}
                  </TabsContent>
                  
                  <TabsContent value="kpis">
                    {renderSection("Key Performance Indicators", result.kpis)}
                    {renderSection("Success Metrics", result.successMetrics)}
                  </TabsContent>
                </div>
              </Tabs>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                <Rocket className="h-16 w-16 mb-4 opacity-50" />
                <p>{t.preview}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}